import React, { Component, Fragment } from 'react';
import { Link } from 'react-router-dom';
import { connect } from 'react-redux';
import cn from 'classnames';
import moment from 'moment';
import 'moment/locale/ru';
import debounce from 'lodash/debounce';

import { inflect } from '../../utils/utilsGlobal';
import { getMessageSnippet, getUserFullName } from '../../utils/utilsChat';
import { linkJiraTaskWithIssue, unlinkJiraTaskFromIssue, updateIssue } from '../../services/apiIssues';
import { searchJiraTasks } from '../../services/apiChat';

import {
  Tooltip,
  Dropdown,
  FormattedMarkdown,
  TextareaWithFormatting,
} from '../../components';

import uiStyles from '../../styles/ui.scss';
import styles from './IssueDetailsSidebar.scss';


const mapStateToProps = state => {
  return {
    currentCompany: state.currentCompany,
  };
};

@connect(mapStateToProps)
export class IssueDetailsSidebar extends Component {

  state = {
    issue: null,
    isEditingNotes: false,
    isSavingNotes: false,
    jiraSearchQuery: '',
    jiraAutocompleteItems: [],
    isLinkingToJiraTask: false,
    linkedTaskProviderIds: [],
  };

  notesTextarea = React.createRef();
  linkToJiraTaskInput = React.createRef();

  debouncedOnLinkToJiraTaskSearch = debounce(this.onLinkToJiraTaskSearch.bind(this), 400);

  componentDidMount(){
    const { issue } = this.props;
    this.setState({
      issue,
      linkedTaskProviderIds: issue.linkedTasks.map(task => task.providerId),
    });
    document.addEventListener('keydown', this.onDocumentKeyDown);
  }

  componentWillUnmount(){
    document.removeEventListener('keydown', this.onDocumentKeyDown);
  }

  onDocumentKeyDown = (e) => {
    const { isEditingNotes } = this.state;
    if (e.key === 'Escape' && isEditingNotes) {
      this.setState({ isEditingNotes: false });
    }
  };

  onRoomNotesKeyUp = (e) => {
    if (e.key === 'Escape') {
      this.setState({ isEditingNotes: false });
    }
  };

  onRoomNotesChange = (e) => {
    const { issue } = this.state;
    const notes = e.target.value;
    this.setState({
      issue: {
        ...issue,
        notes,
      }
    });
  };

  onRoomNotesEditClick = async () => {
    this.setState({ isEditingNotes: true }, () => {
      this.notesTextarea.current.element.focus();
    });
  };

  onRoomNotesSaveClick = () => {
    const { currentCompany } = this.props;
    const { issue } = this.state;
    const trimmedNotes = issue.notes.trim();

    this.setState({
      isEditingNotes: false,
      isSavingNotes: true,
    });
    updateIssue(currentCompany.id, issue.id, { notes: trimmedNotes }).finally(() => {
      this.setState({ isSavingNotes: false });
    });
  };

  getHistoryItemContent = (historyItem) => {
    const { onMassMessageClick } = this.props;
    const dict = {
      'CREATED': 'Создана',
      'CLOSED': 'Решена',
      'REOPENED': 'Переоткрыта',
      'ADDED_TASK': 'Привязана к задаче',
      'UNLINKED_TASK': 'Отвязана от задачи',
      'SENT_MASS_MESSAGE': 'Массовое сообщение',
    };
    const label = dict[historyItem.status] || historyItem.status;

    if (historyItem.status === 'ADDED_TASK' || historyItem.status === 'UNLINKED_TASK') {
      return (
        <Fragment>
          {label} <a className={uiStyles.linkBlack} href={historyItem.task.url} rel="noreferrer noopener" target="_blank">
            {historyItem.task.providerId} {historyItem.task.title}
          </a>
        </Fragment>
      );
    }
    if (historyItem.status === 'SENT_MASS_MESSAGE') {
      return (
        <Fragment>
          {label} <span className={uiStyles.linkBlack} onClick={() => onMassMessageClick(historyItem.massMessage)}>
            «{getMessageSnippet(historyItem.massMessage)}»
          </span>
        </Fragment>
      );
    }
    return label;
  };

  onLinkToJiraTaskOpen = () => {
    requestAnimationFrame(() => {
      this.linkToJiraTaskInput.current?.focus?.()
    });
  };

  onLinkToJiraTaskSearch(query){
    const { currentCompany } = this.props;
    const { linkedTaskProviderIds } = this.state;

    return searchJiraTasks(currentCompany.id, query).then(tasks => {
      const jiraAutocompleteItems = tasks
        .filter(item => !linkedTaskProviderIds.includes(item.providerId))
        .map(item => {
          return {
            title: item.providerId + ': ' + item.title,
            value: item.providerId,
          };
        });
      this.setState({ jiraAutocompleteItems });
    });
  }

  onLinkToJiraTaskSelect = (data) => {
    const { currentCompany, onHistoryChange } = this.props;
    const { issue, linkedTaskProviderIds } = this.state;
    const jiraTaskProviderId = data.value;
    this.setState({ isLinkingToJiraTask: true });

    return linkJiraTaskWithIssue(currentCompany.id, issue.id, jiraTaskProviderId)
      .then(task => {
        this.setState({
          issue: {
            ...issue,
            linkedTasks: [ ...issue.linkedTasks, task ],
          },
          jiraSearchQuery: '',
          jiraAutocompleteItems: [],
          linkedTaskProviderIds: [ ...linkedTaskProviderIds, jiraTaskProviderId ],
        });
        onHistoryChange && onHistoryChange();
      })
      .finally(() => {
        this.setState({ isLinkingToJiraTask: false });
      });
  };

  onUnlinkFromJiraClick = (taskId) => {
    const { currentCompany } = this.props;
    const { issue } = this.state;
    if ( confirm('Отвязать проблему от задачи Jira?') ) {
      unlinkJiraTaskFromIssue(currentCompany.id, issue.id, taskId).then(() => {
        this.setState({
          issue: {
            ...issue,
            linkedTasks: issue.linkedTasks.filter(task => task.id !== taskId),
          }
        });
      });
    }
  };

  render(){
    const { className, history } = this.props;
    const {
      issue,
      isEditingNotes,
      isSavingNotes,
      jiraSearchQuery,
      jiraAutocompleteItems,
      isLinkingToJiraTask,
    } = this.state;

    if (!issue || null)  {
      return null;
    }

    const trimmedNotes = issue.notes.trim();

    return (
      <div className={cn(styles.container, className)}>

        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>Комментарий</h3>
          {isEditingNotes && (
            <Fragment>
              <TextareaWithFormatting
                className={styles.notesTextarea}
                placeholder="Заметки о проблеме"
                ref={this.notesTextarea}
                value={issue.notes}
                minRows={2}
                maxRows={10}
                onKeyUp={this.onRoomNotesKeyUp}
                onChange={this.onRoomNotesChange}>
              </TextareaWithFormatting>
              <div>
                <button className={uiStyles.buttonSecondary} onClick={this.onRoomNotesSaveClick}>
                  Сохранить
                </button>
              </div>
            </Fragment>
          )}

          {!isEditingNotes && (
            <Fragment>
              {Boolean(trimmedNotes) && (
                <FormattedMarkdown className={styles.notesText} markdown={trimmedNotes}/>
              )}
              <div>
                <button
                  className={cn({
                    [uiStyles.buttonSecondary]: true,
                    [styles.notesButtonAdd]: !trimmedNotes,
                  })}
                  onClick={this.onRoomNotesEditClick}
                  disabled={isSavingNotes}>
                  {isSavingNotes ? 'Сохранение...' : (trimmedNotes ? 'Изменить' : 'Добавить')}
                </button>
              </div>
            </Fragment>
          )}
        </div>

        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>Связанные задачи</h3>
          <ul className={styles.tasks}>
            {issue.linkedTasks.map(task => (
              <li className={styles.tasksItem} key={task.id}>
                <a className={cn(styles.tasksTitle, uiStyles.linkBlack)} href={task.url} rel="noreferrer noopener" target="_blank">
                  {task.providerId} {task.title}
                </a>
                <div>
                  <Link to={`/issues?linkedTo=${task.providerId}`}>
                    В {inflect(task.issuesCount, ['проблеме', 'проблемах', 'проблемах'])}
                  </Link>
                  <span className={styles.tasksDetailsSeparator}>&middot;</span>
                  <span className={styles.tasksDetailsLink} onClick={() => this.onUnlinkFromJiraClick(task.id)}>
                    Отвязать
                  </span>
                </div>
              </li>
            ))}
          </ul>

          <Dropdown
            overrideClassNames={{
              list: styles.taskSearchList,
              item: styles.taskSearchItem,
            }}
            items={jiraAutocompleteItems}
            onChange={this.onLinkToJiraTaskSelect}
            renderListTop={() => (
              <input
                className={styles.taskSearchInput}
                ref={this.linkToJiraTaskInput}
                type="text"
                placeholder="Поиск задачи в Jira"
                value={jiraSearchQuery}
                onChange={(e) => {
                  this.setState({ jiraSearchQuery: e.target.value });
                  this.debouncedOnLinkToJiraTaskSearch(e.target.value);
                }}/>
            )}>
            <button className={cn({
              [uiStyles.buttonSecondary]: true,
              [styles.tasksIsLinking]: isLinkingToJiraTask,
            })} onClick={this.onLinkToJiraTaskOpen}>
              Связать с задачей в Jira
            </button>
          </Dropdown>
        </div>

        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>История работы</h3>
          <ul className={styles.history}>
            {history.map(historyItem => (
              <li className={styles.historyItem} key={historyItem.id}>
                <div className={styles.historyStatus}>{this.getHistoryItemContent(historyItem)}</div>
                <div className={styles.historyDetails}>
                  <Tooltip title={moment(historyItem.timestamp).format('D MMMM YYYY, H:mm:ss')}>
                    <span>{moment(historyItem.timestamp).format('D MMMM YYYY')},&nbsp;</span>
                  </Tooltip>
                  {getUserFullName(historyItem.changedBy)}
                </div>
              </li>
            ))}
          </ul>
        </div>

      </div>
    );
  }
}
